import React from 'react';
import { IntelligentSearchResult } from '../services/intelligentSearchService';
import {
  getJobImportActionLabel,
  useSearchResultApplicationImport,
} from '../hooks/useSearchResultApplicationImport';
import './IntelligentSearchModal.css';

export interface IntelligentSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: IntelligentSearchResult | null;
  isLoading: boolean;
  error: string | null;
  title?: string;
  resultsLabel?: string;
}

export const IntelligentSearchModal: React.FC<IntelligentSearchModalProps> = ({
  isOpen,
  onClose,
  results,
  isLoading,
  error,
  title,
  resultsLabel,
}) => {
  const searchResults = results?.results ?? [];
  const {
    saveStatusByUrl,
    saveErrorByUrl,
    handleAddToApplications,
  } = useSearchResultApplicationImport({
    isEnabled: isOpen,
    results: searchResults,
  });

  // Prevent body scroll when panel is open
  React.useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="intelligent-search-panel-overlay"
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intelligent-search-panel-title"
        tabIndex={-1}
      />
      
      {/* Slide-out panel */}
      <div className="intelligent-search-panel">
        <div className="intelligent-search-panel-header">
          <h2 id="intelligent-search-panel-title" className="intelligent-search-panel-title">
            {title || "Intelligent Search Results"}
            {results?.searchQuery && (
              <span className="intelligent-search-query-badge">
                "{results.searchQuery}"
              </span>
            )}
          </h2>
          <button
            className="intelligent-search-panel-close"
            onClick={onClose}
            aria-label="Close search results"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="intelligent-search-panel-body">
          {isLoading && (
            <div className="intelligent-search-loading">
              <div className="intelligent-search-spinner"></div>
              <p>Our AI is combing the web for the insights you asked for... This usually takes about 30-60 seconds.</p>
            </div>
          )}

          {error && (
            <div className="intelligent-search-error">
              <p className="error-message">{error}</p>
            </div>
          )}

          {results && !error && (
            <div className="intelligent-search-results">
              {results.summary && (
                <div className="intelligent-search-summary">
                  <h3>Summary</h3>
                  <p>{results.summary}</p>
                </div>
              )}

              {results.results && results.results.length > 0 ? (
                <div className="intelligent-search-results-list">
                  <h3>{resultsLabel || "Search Results"} ({results.results.length})</h3>
                  <ul>
                    {results.results.map((result, index) => (
                      <li key={index} className="intelligent-search-result-item">
                        <div className="intelligent-search-result-content">
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="intelligent-search-result-link"
                          >
                            <h4>{result.title || 'Untitled'}</h4>
                            <p className="intelligent-search-result-snippet">{result.snippet || 'No description available'}</p>
                          </a>
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="intelligent-search-job-link"
                            title={result.url}
                          >
                            Job Link
                          </a>
                          {result.relevance !== undefined && (
                            <span className="intelligent-search-relevance">
                              Relevance: {result.relevance}%
                            </span>
                          )}
                        </div>
                        <div className="intelligent-search-result-actions">
                          <button
                            type="button"
                            className="intelligent-search-add-button"
                            onClick={() => handleAddToApplications(result)}
                            disabled={
                              !result.url
                              || saveStatusByUrl[result.url] === 'checking'
                              || saveStatusByUrl[result.url] === 'saving'
                              || saveStatusByUrl[result.url] === 'saved'
                              || saveStatusByUrl[result.url] === 'exists'
                            }
                          >
                            {getJobImportActionLabel(saveStatusByUrl[result.url] || 'idle')}
                          </button>
                          {saveStatusByUrl[result.url] === 'saved' && (
                            <span className="intelligent-search-add-status">
                              Added to job applications
                            </span>
                          )}
                          {saveStatusByUrl[result.url] === 'exists' && (
                            <span className="intelligent-search-add-status">
                              Already added to job applications
                            </span>
                          )}
                        </div>
                        {saveStatusByUrl[result.url] === 'error' && saveErrorByUrl[result.url] && (
                          <p className="intelligent-search-add-error">
                            {saveErrorByUrl[result.url]}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="intelligent-search-empty">
                  <p>No results found. Try adjusting your search criteria.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
